import { useEffect, useState } from "react";
import { Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminFormDialog from "./AdminFormDialog";
import DeleteUserDialog from "./DeleteUserDialog";
import UserTable from "./UserTable";

export default function AdminUsersSection() {
  const { user, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/users/admins");
    setUsers(data.users);
  };

  useEffect(() => {
    load().catch(() => toast.error("Gagal memuat daftar admin")).finally(() => setLoading(false));
  }, []);

  const save = async (values) => {
    setSaving(true);
    try {
      if (selected) {
        await api.patch(`/admin/users/admins/${selected.id}`, values);
        if (selected.id === user.id) await refreshUser();
        toast.success("Data admin diperbarui");
      } else {
        await api.post("/admin/users/admins", values);
        toast.success("Admin baru ditambahkan");
      }
      setFormOpen(false);
      await load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (account) => {
    try {
      await api.delete(`/admin/users/admins/${account.id}`);
      toast.success("Admin dihapus");
      await load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const view = async (account) => {
    try {
      const { data } = await api.get(`/admin/users/admins/${account.id}`);
      setDetail(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  return (
    <section className="border border-border bg-card p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /> Admin</h2>
          <p className="text-xs text-muted-foreground mt-1">Kelola akun dengan akses administrator.</p>
        </div>
        <Button type="button" onClick={() => { setSelected(null); setFormOpen(true); }} className="rounded-none"><Plus size={16} /> Tambah Admin</Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground py-6">Memuat admin...</p> : (
        <UserTable users={users} emptyText="Belum ada admin." renderActions={(account) => <>
          <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => view(account)}>Lihat</Button>
          <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => { setSelected(account); setFormOpen(true); }}>Edit</Button>
          <DeleteUserDialog account={account} onDelete={remove} disabled={account.id === user.id} />
        </>} />
      )}
      <AdminFormDialog account={selected} open={formOpen} onOpenChange={setFormOpen} onSave={save} saving={saving} />
      <Dialog open={Boolean(detail)} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Detail Admin</DialogTitle><DialogDescription>Informasi akun administrator.</DialogDescription></DialogHeader>
          {detail && <dl className="grid grid-cols-[90px_1fr] gap-y-3 text-sm break-all">
            <dt className="text-muted-foreground">Nama</dt><dd>{detail.name}</dd>
            <dt className="text-muted-foreground">Email</dt><dd>{detail.email}</dd>
            <dt className="text-muted-foreground">Role</dt><dd>{detail.role}</dd>
            <dt className="text-muted-foreground">Dibuat</dt><dd>{detail.created_at ? new Date(detail.created_at).toLocaleString("id-ID") : "—"}</dd>
          </dl>}
        </DialogContent>
      </Dialog>
    </section>
  );
}
